// YagiprimePaymentService.js
// ヤギプライム課金関連の処理を集約したサービス

import {
  trackYagiprimePaypayAuthorized,
  trackYagiprimePurchase
} from '@/composables/useYagiprimePurchaseTracking'

export default {
  install (Vue) {
    // グローバルメソッドとして登録
    Vue.prototype.$yagiprimePaymentService = {
      // ログレベルの定義
      LOG_LEVELS: {
        INFO: 'INFO',
        WARN: 'WARN',
        ERROR: 'ERROR',
        DEBUG: 'DEBUG'
      },

      // ログ出力関数
      _log (level, message, data = null) {
        const timestamp = new Date().toISOString()
        const logMessage = `[YagiprimePayment][${level}][${timestamp}] ${message}`

        switch (level) {
          case this.LOG_LEVELS.ERROR:
            console.error(logMessage, data)
            break
          case this.LOG_LEVELS.WARN:
            console.warn(logMessage, data)
            break
          case this.LOG_LEVELS.DEBUG:
            console.debug(logMessage, data)
            break
          default:
            console.log(logMessage, data)
        }
      },

      // 機能タイプに対応する表示名
      featureNames: {
        'font-payment': 'フォント変更',
        'font-scope-all': 'フォント適用範囲（固定テキストも含む）',
        'transparency-image': '透過画像処理',
        self_introduction: '自己PR作成',
        'yagiprime-agent': 'ヤギプライムエージェント',
        'multiple-reasons': '志望動機の複数保存'
      },

      // 内部状態管理
      _state: {
        currentFeature: null,
        pendingFeatures: {},
        isProcessing: false
      },

      /**
       * ヤギプライム機能を使用する - 統合された一つのエントリーポイント
       * @param {Object} vm - Vueコンポーネントのインスタンス
       * @param {String} featureType - 機能タイプ（例: 'font-payment'）
       * @param {Object} options - オプション設定
       * @param {Function} options.onSuccess - 成功時のコールバック
       * @param {Function} options.onCancel - キャンセル時のコールバック
       * @param {Function} options.onError - エラー時のコールバック
       * @param {Function} options.onAlreadyPremium - 既に課金済みの場合に呼び出すコールバック
       * @param {Object} options.data - 機能固有の追加データ
       * @returns {Promise} 処理結果のPromise
       */
      usePremiumFeature (vm, featureType, options = {}) {
        this._log(this.LOG_LEVELS.INFO, `プレミアム機能使用開始: ${featureType}`, { options })

        const {
          onSuccess,
          onCancel,
          onError,
          onAlreadyPremium,
          data = {}
        } = options

        // コールバックを登録
        if (onSuccess) {
          this._log(this.LOG_LEVELS.DEBUG, `成功時コールバック登録: ${featureType}`)
          this.storeCallbackForFeature(vm, featureType, onSuccess)
        }
        if (onCancel) {
          this._log(this.LOG_LEVELS.DEBUG, `キャンセル時コールバック登録: ${featureType}`)
          this.storeCallbackForFeature(vm, `${featureType}-cancel`, onCancel)
        }
        if (onError) {
          this._log(this.LOG_LEVELS.DEBUG, `エラー時コールバック登録: ${featureType}`)
          this.storeCallbackForFeature(vm, `${featureType}-error`, onError)
        }

        // 状態管理のための情報を保存
        this._state.pendingFeatures[featureType] = {
          vm,
          data,
          timestamp: Date.now()
        }
        // 現在の機能タイプを設定
        this._state.currentFeature = featureType
        this._log(this.LOG_LEVELS.DEBUG, `保留中機能を保存: ${featureType}`, this._state.pendingFeatures[featureType])

        // ヤギプライムユーザーなら即時実行して完了
        if (vm.$globalVar.isYagiPrime) {
          this._log(this.LOG_LEVELS.INFO, `ヤギプライム会員確認済み: ${featureType}`)
          if (onAlreadyPremium) {
            onAlreadyPremium(data)
          } else if (onSuccess) {
            onSuccess(data)
          }
          return Promise.resolve({ status: 'success', message: 'ヤギプライム会員です' })
        }

        // ユーザー認証状態に基づいて処理を分岐
        if (!vm.$firebase.isAuthenticated) {
          this._log(this.LOG_LEVELS.INFO, `未認証ユーザー: ${featureType} - ログインフローへ`)
          // 未認証の場合はログインから
          this.setLoginContext(vm, featureType)
          this.showLoginDialog(vm)
          return Promise.resolve({
            status: 'pending',
            message: 'ログインが必要です',
            featureType
          })
        }

        this._log(this.LOG_LEVELS.INFO, `認証済み未課金ユーザー: ${featureType} - 課金フローへ`)
        // 認証済みだが未課金の場合は課金ダイアログへ
        this.handleRouteId(vm, featureType)
        this.showPaymentDialog(vm)
        return Promise.resolve({
          status: 'pending',
          message: '課金が必要です',
          featureType
        })
      },

      /**
       * 汎用プレミアム機能を使用する（値の比較が必要な場合に使用）
       * @param {Object} vm - Vueコンポーネントのインスタンス
       * @param {String} featureType - 機能タイプ（例: 'font-payment'）
       * @param {*} currentValue - 現在選択されている値
       * @param {*} defaultValue - デフォルト値（この値の場合は課金不要）
       * @param {Object} options - コールバックを含むオプション
       * @returns {Promise} 処理結果のPromise
       */
      useFeatureWithDefault (vm, featureType, currentValue, defaultValue, options = {}) {
        // デフォルト値の場合は課金不要
        if (currentValue === defaultValue) {
          if (options.onSuccess) options.onSuccess()
          return Promise.resolve({
            status: 'success',
            message: 'デフォルト値は課金不要です'
          })
        }

        // 機能特有のデータを追加
        const featureOptions = {
          ...options,
          data: {
            ...options.data,
            currentValue,
            defaultValue
          }
        }

        // 課金フロー開始
        return this.usePremiumFeature(vm, featureType, featureOptions)
      },

      /**
       * 汎用プレミアム機能を使用する（特定条件に基づいて課金判断）
       * @param {Object} vm - Vueコンポーネントのインスタンス
       * @param {String} featureType - 機能タイプ
       * @param {Function} shouldChargeCheck - 課金が必要かどうかを判断する関数
       * @param {Object} options - コールバックを含むオプション
       * @returns {Promise} 処理結果のPromise
       *
       * 使用例:
       * // 複雑な条件で課金判断する場合
       * this.$yagiprimePaymentService.useFeatureWithCondition(
       *   this,
       *   'custom-feature',
       *   () => this.selectedOptions.length > 3 || this.isAdvancedMode,
       *   {
       *     onSuccess: () => { this.processFeature() },
       *     onCancel: () => { this.resetSelections() }
       *   }
       * )
       */
      useFeatureWithCondition (vm, featureType, shouldChargeCheck, options = {}) {
        // 課金が必要かどうかチェック
        if (!shouldChargeCheck()) {
          if (options.onSuccess) options.onSuccess()
          return Promise.resolve({
            status: 'success',
            message: '課金不要の機能です'
          })
        }

        // 課金フロー開始
        return this.usePremiumFeature(vm, featureType, options)
      },

      /**
       * 条件分岐のない簡素化メソッド
       * @param {*} vm - Vueコンポーネントのインスタンス
       * @param {*} featureType - 機能タイプ
       * @param {*} options - オプション
       * @returns {Promise} 処理結果のPromise
       */
      useFeature (vm, featureType, options = {}) {
        return this.usePremiumFeature(vm, featureType, options)
      },

      // 特定の機能タイプに対するメソッドを動的に生成するヘルパー関数
      _createFeatureMethod (featureType) {
        return function (vm, options = {}) {
          return this.usePremiumFeature(vm, featureType, options)
        }
      },

      // 初期化時に各機能メソッドを動的に生成
      init (vm) {
        // 各種リスナーを登録
        this.registerPaymentResultListener(vm)
        this.registerLoginResultListener(vm)

        // PayPay決済完了後のリダイレクト処理
        this.checkPaypayRedirect(vm)

        // Stripe決済完了後のリダイレクト処理
        this.checkStripeRedirect(vm)

        // 汎用課金成功イベントのリスナーを登録
        vm.$eventHub.$on('yagiprime-payment-success', () => {
          // 必要に応じて追加処理
        })

        // 既存の機能タイプに対するメソッドを動的に生成
        const featureTypes = Object.keys(this.featureNames)
        featureTypes.forEach(type => {
          const methodName = `use${type.split('-').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join('')}`

          // メソッドがまだ定義されていない場合のみ生成
          if (!this[methodName]) {
            this[methodName] = this._createFeatureMethod(type)
          }
        })
      },

      /**
       * PayPay決済完了後のリダイレクトをチェックする
       * @param {Object} vm - Vueコンポーネントのインスタンス
       */
      checkPaypayRedirect (vm) {
        // URLクエリパラメータをチェック
        if (vm.$route && vm.$route.query && vm.$route.query.userAuthorizationId) {
          this._log(this.LOG_LEVELS.INFO, 'PayPay決済完了後のリダイレクトを検出しました')

          // PayPay はここで分かるのは認可完了までで、実課金は webhook 非同期のため
          // purchase ではなく専用イベントとして送る
          trackYagiprimePaypayAuthorized(vm.$gtm, {
            authorizationId: vm.$route.query.userAuthorizationId
          })

          // 決済成功イベントを発行
          setTimeout(() => {
            vm.$eventHub.$emit('payment_yagiprime_result', 'success')
          }, 100)

          // URLからクエリパラメータを削除
          if (vm.$router) {
            vm.$router.replace({ path: vm.$route.path })
          }
        }
      },

      /**
       * Stripe決済完了後のリダイレクトをチェックする
       * @param {Object} vm - Vueコンポーネントのインスタンス
       */
      checkStripeRedirect (vm) {
        // URLクエリパラメータをチェック
        if (vm.$route && vm.$route.query &&
            vm.$route.query.payment_intent &&
            vm.$route.query.redirect_status === 'succeeded') {
          this._log(this.LOG_LEVELS.INFO, 'Stripe決済完了後のリダイレクトを検出しました')

          // ApplePay / 3DS の課金確定点
          trackYagiprimePurchase(vm.$gtm, {
            transactionId: vm.$route.query.payment_intent,
            uuid:
              vm.$firebase && vm.$firebase.user ? vm.$firebase.user.uid : null
          })

          // 決済成功イベントを発行
          setTimeout(() => {
            vm.$eventHub.$emit('payment_yagiprime_result', 'success')
          }, 100)

          // URLからクエリパラメータを削除
          if (vm.$router) {
            vm.$router.replace({ path: vm.$route.path })
          }
        }
      },

      /**
       * コールバック関数を一時的に保存する
       * @param {Object} vm - Vueコンポーネントのインスタンス
       * @param {String} featureType - 機能タイプ
       * @param {Function} callback - コールバック関数
       */
      storeCallbackForFeature (vm, featureType, callback) {
        // グローバルコールバック保存用のオブジェクトがなければ初期化
        if (!vm.$globalVar._yagiprimeCallbacks) {
          vm.$globalVar._yagiprimeCallbacks = {}
        }
        // 機能タイプをキーとしてコールバックを保存
        vm.$globalVar._yagiprimeCallbacks[featureType] = callback
      },

      /**
       * 保存されたコールバックを取得して実行する
       * @param {Object} vm - Vueコンポーネントのインスタンス
       * @param {String} featureType - 機能タイプ
       * @returns {Boolean} コールバックが実行されたかどうか
       */
      executeStoredCallback (vm, featureType) {
        // コールバック保存用オブジェクトがなければ終了
        if (!vm.$globalVar._yagiprimeCallbacks) {
          return false
        }
        // 保存されたコールバックを取得
        const callback = vm.$globalVar._yagiprimeCallbacks[featureType]
        // コールバックがあれば実行して削除
        if (typeof callback === 'function') {
          callback()
          // 実行後はコールバックを削除（1回限りの実行）
          delete vm.$globalVar._yagiprimeCallbacks[featureType]
          return true
        }
        return false
      },

      /**
       * ログインコンテキストを設定する
       * @param {Object} vm - Vueコンポーネントのインスタンス
       * @param {String} featureType - 機能タイプ
       */
      setLoginContext (vm, featureType) {
        // イベントバスを使用してコンテキストを設定
        vm.$eventHub.$emit('set-login-context', featureType)
        return true
      },

      /**
       * ログインダイアログを表示する
       * @param {Object} vm - Vueコンポーネントのインスタンス
       */
      showLoginDialog (vm) {
        if (vm.$modal) {
          vm.$modal.show('login-dialog')
        } else {
          console.error('[YagiprimePaymentService] モーダル機能が利用できません')
        }
      },

      /**
       * ルートIDを処理する
       * @param {Object} vm - Vueコンポーネントのインスタンス
       * @param {String} featureType - 機能タイプ
       */
      handleRouteId (vm, featureType) {
        const routeId = vm.$cookies.get('_YAGISH_YAGIPRIME_ROUTE_ID')
        if (routeId) {
          vm.$cookies.set('_YAGISH_YAGIPRIME_ROUTE_ID', routeId + '#' + featureType, null, null, 'yagish.jp')
          vm.$cookies.set('_YAGISH_YAGIPRIME_ROUTE_ID', routeId + '#' + featureType, null, null, 'localhost')
        } else {
          vm.$cookies.set('_YAGISH_YAGIPRIME_ROUTE_ID', featureType, null, null, 'yagish.jp')
          vm.$cookies.set('_YAGISH_YAGIPRIME_ROUTE_ID', featureType, null, null, 'localhost')
        }
      },

      /**
       * 課金ダイアログを表示する
       * @param {Object} vm - Vueコンポーネントのインスタンス
       */
      showPaymentDialog (vm) {
        if (vm.$modal) {
          const modalName = 'yagiprime-payment-dialog'
          vm.$modal.show(modalName)
        } else {
          console.error('[YagiprimePaymentService] モーダル機能が利用できません')
        }
      },

      /**
       * ログインダイアログが閉じられた後の処理を行う
       * @param {Object} vm - Vueコンポーネントのインスタンス
       * @param {String} loginContext - ログインコンテキスト
       */
      handleLoginDialogClosed (vm, loginContext) {
        // ユーザー認証状態を確認
        if (vm.$firebase.isAuthenticated) {
          // 保留中の機能があれば、課金ダイアログに進む
          if (loginContext && this._state.pendingFeatures[loginContext]) {
            // 課金ダイアログ表示
            this.handleRouteId(vm, loginContext)
            this.showPaymentDialog(vm)
          }
        } else {
          // 認証キャンセルまたは失敗の場合、キャンセルコールバックを実行
          if (loginContext) {
            this.executeStoredCallback(vm, `${loginContext}-cancel`)
            // 保留中の機能もクリア（再度ログインダイアログが表示されるのを防ぐ）
            delete this._state.pendingFeatures[loginContext]
          }
        }

        // コンテキストをリセット
        this._state.currentFeature = null
      },

      /**
       * 課金結果を処理する
       * @param {Object} vm - Vueコンポーネントのインスタンス
       * @param {String} result - 課金結果 ('success' または 'error')
       */
      handlePaymentResult (vm, result) {
        this._log(this.LOG_LEVELS.INFO, '課金結果処理開始', { result })

        // 現在処理中の機能タイプを取得（pendingFeaturesから最新のものを取得）
        const featureType = this._state.currentFeature ||
          Object.keys(this._state.pendingFeatures).sort((a, b) =>
            this._state.pendingFeatures[b].timestamp - this._state.pendingFeatures[a].timestamp
          )[0]

        if (!featureType) {
          this._log(this.LOG_LEVELS.ERROR, '処理中の機能タイプが見つかりません')
          return
        }
        this._log(this.LOG_LEVELS.DEBUG, `処理中の機能タイプ: ${featureType}`)

        // 保留中の機能情報を取得
        const featureInfo = this._state.pendingFeatures[featureType]
        if (!featureInfo) {
          this._log(this.LOG_LEVELS.ERROR, `機能情報が見つかりません: ${featureType}`)
          return
        }
        this._log(this.LOG_LEVELS.DEBUG, '保留中機能情報', featureInfo)

        if (result !== 'success') {
          this._log(this.LOG_LEVELS.WARN, `課金失敗: ${featureType}`)
          // 課金失敗時は汎用イベントを発行
          vm.$eventHub.$emit('yagiprime-payment-failed')
          // キャンセル時のコールバックを実行
          this.executeStoredCallback(vm, `${featureType}-cancel`)
          // 課金失敗時もモーダルを閉じる
          this.closeAllPaymentModals(vm)
          return
        }

        this._log(this.LOG_LEVELS.INFO, `課金成功: ${featureType}`)
        // グローバル変数を更新（ヤギプライムフラグを有効化）
        vm.$globalVar.isYagiPrime = true

        // 汎用イベントを発行
        vm.$eventHub.$emit('yagiprime-payment-success', featureType)

        // すべての関連モーダルを閉じる
        this.closeAllPaymentModals(vm)

        // 決済完了ポップアップを表示（コールバック付き）
        this.showCompletePopup(vm, featureType, featureInfo)
      },

      /**
       * 決済完了ポップアップを表示する
       * @param {Object} vm - Vueコンポーネントのインスタンス
       * @param {String} featureType - 機能タイプ
       * @param {Object} featureInfo - 機能情報
       */
      showCompletePopup (vm, featureType, featureInfo) {
        this._log(this.LOG_LEVELS.INFO, `決済完了ポップアップを表示: ${featureType}`)

        // 保存されたコールバックを取得
        const savedCallback = vm.$globalVar._yagiprimeCallbacks &&
                            vm.$globalVar._yagiprimeCallbacks[featureType]

        // 完了ポップアップが閉じられた時のコールバック関数を定義
        const completeCallback = () => {
          this._log(this.LOG_LEVELS.DEBUG, `完了ポップアップが閉じられました: ${featureType}`)

          // 元のコールバック関数を実行
          if (typeof savedCallback === 'function') {
            this._log(this.LOG_LEVELS.DEBUG, `成功時コールバック実行: ${featureType}`)
            try {
              savedCallback(featureInfo.data || {})
            } catch (error) {
              this._log(this.LOG_LEVELS.ERROR, `コールバック実行エラー: ${featureType}`, error)
            }
            // 実行後はコールバックを削除
            delete vm.$globalVar._yagiprimeCallbacks[featureType]
          }

          // 処理が完了した機能をクリーンアップ
          delete this._state.pendingFeatures[featureType]
          this._log(this.LOG_LEVELS.DEBUG, `保留中機能をクリーンアップ: ${featureType}`)
        }

        // 決済完了ポップアップを表示
        vm.$eventHub.$emit('show-prime-complete-popup', completeCallback)
      },

      /**
       * すべての課金関連モーダルを閉じる
       * @param {Object} vm - Vueコンポーネントのインスタンス
       */
      closeAllPaymentModals (vm) {
        if (!vm.$modal) {
          console.error('[YagiprimePaymentService] モーダル機能が利用できません')
          return
        }

        // 課金関連モーダルの一覧
        const paymentModals = [
          'yagiprime-payment-dialog', // メイン課金ダイアログ
          'yagiprimefontpayment-dialog', // フォント課金ダイアログ
          'login-dialog' // ログインダイアログ
        ]

        // すべてのモーダルを閉じる
        paymentModals.forEach(modalName => {
          try {
            vm.$modal.hide(modalName)
          } catch (e) {
            console.error(`[YagiprimePaymentService] モーダル ${modalName} の閉じる処理に失敗しました`, e)
          }
        })

        // 汎用メッセージを発行（共通処理のため）
        vm.$eventHub.$emit('payment-modals-closed')
      },

      /**
       * 課金結果イベントリスナーを登録する
       * @param {Object} vm - Vueコンポーネントのインスタンス
       */
      registerPaymentResultListener (vm) {
        // 既存のリスナーを削除（重複登録を防止）
        vm.$eventHub.$off('payment_yagiprime_result')
        // 新しいリスナーを登録
        vm.$eventHub.$on('payment_yagiprime_result', (result) => {
          this.handlePaymentResult(vm, result)
        })
      },

      /**
       * ログインダイアログ結果リスナーを登録する
       * @param {Object} vm - Vueコンポーネントのインスタンス
       */
      registerLoginResultListener (vm) {
        // 既存のリスナーを削除
        vm.$eventHub.$off('login-dialog-closed')
        // 新しいリスナーを登録
        vm.$eventHub.$on('login-dialog-closed', () => {
          const loginContext = this._state.currentFeature
          this.handleLoginDialogClosed(vm, loginContext)
        })
      },

      /**
       * サービスのクリーンアップ（コンポーネントのdestroyedで呼び出す）
       * @param {Object} vm - Vueコンポーネントのインスタンス
       */
      cleanup (vm) {
        this._log(this.LOG_LEVELS.INFO, 'サービスのクリーンアップ開始')
        // リスナーを解除
        vm.$eventHub.$off('payment_yagiprime_result')
        vm.$eventHub.$off('login-dialog-closed')
        vm.$eventHub.$off('yagiprime-payment-success')
        this._log(this.LOG_LEVELS.INFO, 'サービスのクリーンアップ完了')
      }
    }
  }
}
